import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { fetchVersion } from '../api';
import { qk } from '../api/queryKeys';

/** Gap below the trigger and minimum breathing room from the viewport edges. */
const GAP = 6;
const EDGE = 16;
const MAX_WIDTH = 320; // matches the desktop w-80 panel

/**
 * Header control showing the deployed build version (e.g. "v1.1302"), so you can
 * tell at a glance whether a new build is live. Re-polls every 60s, so if a
 * deploy lands while the tab is open the number updates without a manual reload.
 *
 * Clicking it opens a small dropdown *downward* listing the last 5 commits baked
 * into the running build — short hash + subject. The commits reflect what is
 * ACTUALLY deployed, not main's tip. When not built by CI (local dev) the list
 * is empty and the dropdown just shows the build meta.
 *
 * The badge sits at the far LEFT of the header, so a left-anchored dropdown would
 * overflow the right edge on a phone. The menu is therefore rendered into
 * document.body (escaping the sticky header's backdrop-filter containing block,
 * like AccountMenu/OverflowMenu) at a `fixed` position computed from the trigger
 * rect and clamped to the viewport — so on desktop it hangs under the badge, and
 * on mobile it stays fully on-screen (effectively centered with edge margins).
 */
export function VersionBadge() {
  const { data } = useQuery({
    queryKey: qk.version,
    queryFn: fetchVersion,
    staleTime: 60_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number; width: number } | null>(
    null,
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      const target = e.target as Node;
      if (!buttonRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    // Position is captured on open; close rather than track on scroll/resize.
    function onScrollOrResize() {
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  if (!data) return null;

  function toggle() {
    if (!open && buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      const width = Math.min(MAX_WIDTH, window.innerWidth - EDGE * 2);
      // Anchor to the badge's left edge, but never let the panel run off either side.
      const left = Math.min(Math.max(r.left, EDGE), window.innerWidth - width - EDGE);
      setPosition({ top: r.bottom + GAP, left, width });
    }
    setOpen((o) => !o);
  }

  const commits = data.commits ?? [];
  const meta = [
    data.sha ? `commit ${data.sha}` : null,
    data.builtAt ? `built ${data.builtAt}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Build ${data.version} — show recent commits`}
        onClick={toggle}
        title={meta || undefined}
        className="inline-flex items-center rounded border border-line bg-surface-2 px-1.5 py-0.5 font-mono text-2xs text-ink-faint transition-colors hover:border-line-strong hover:text-ink-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-signal/60"
      >
        {data.version}
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={{ top: position.top, left: position.left, width: position.width }}
            className="fixed z-30 animate-fade-in rounded-[8px] border border-line-strong bg-surface-1 p-1 shadow-lg"
          >
            {commits.length === 0 ? (
              <div className="px-2.5 py-2 text-xs text-ink-muted">
                No commit history in this build{meta ? ` · ${meta}` : ''}.
              </div>
            ) : (
              <ul className="py-0.5">
                {commits.map((c, i) => (
                  <li key={`${c.hash}-${i}`}>
                    <div className="flex items-baseline gap-2 rounded-[5px] px-2.5 py-1.5 hover:bg-surface-2">
                      <span className="shrink-0 font-mono text-2xs text-ink-faint">{c.hash}</span>
                      <span className="min-w-0 truncate text-xs text-ink-muted" title={c.title}>
                        {c.title}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
